import { RTKQueryTag } from 'src/common/constants';
import type { ApiResponse } from 'src/common/interfaces';
import type { RoomBlueprint } from 'src/common/interfaces/room-blueprint';
import { apiSlice } from 'src/features/api/api-slice';

import type {
  GetBlueprintRuleResponse,
  GetBlueprintRulesResponse,
  GetBlueprintTasksResponse,
  SaveBlueprintRequest,
} from './blueprint.types';

export const blueprintApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getBlueprintRules: builder.query<GetBlueprintRulesResponse[], string>({
      query: (roomId: string) => ({
        url: `admin/rooms/${roomId}/rules`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<GetBlueprintRulesResponse[]>) => response.data ?? [],
      providesTags: (_result, _error, roomId) => [{ type: RTKQueryTag.RoomBlueprintRules, roomId }],
    }),

    getBlueprintRule: builder.query<GetBlueprintRuleResponse | undefined, string>({
      query: (ruleId: string) => ({
        url: `admin/rooms/rules/${ruleId}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<GetBlueprintRuleResponse>) => response.data,
      providesTags: (_result, _error, ruleId) => [{ type: RTKQueryTag.RoomBlueprintRules, ruleId }],
    }),

    getBlueprintTasks: builder.query<GetBlueprintTasksResponse[], string>({
      query: (roomId: string) => ({
        url: `admin/rooms/${roomId}/tasks`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<GetBlueprintTasksResponse[]>) => response.data ?? [],
      providesTags: (_result, _error, roomId) => [{ type: RTKQueryTag.RoomBlueprintTasks, roomId }],
    }),

    getBlueprint: builder.query<RoomBlueprint | undefined, string>({
      query: (roomId: string) => ({
        url: `admin/rooms/${roomId}/blueprints`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<RoomBlueprint>) => response.data,
      providesTags: (_result, _error, roomId) => [{ type: RTKQueryTag.RoomBlueprint, roomId }],
    }),

    saveBlueprint: builder.mutation<RoomBlueprint, { roomId: string; payload: SaveBlueprintRequest }>({
      query: ({ roomId, payload }) => ({
        url: `admin/rooms/${roomId}/blueprints`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error) =>
        !error && result ? [{ type: RTKQueryTag.RoomBlueprint, roomId: result.roomId }] : [],
    }),
  }),
});

export const {
  useGetBlueprintRulesQuery,
  useGetBlueprintRuleQuery,
  useGetBlueprintTasksQuery,
  useGetBlueprintQuery,
  useSaveBlueprintMutation,
} = blueprintApiSlice;
